import { Button } from "$components/button/button";
import { SearchField as _SearchBar } from "$components/search-field";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import CreateCustomItemsModal from "$routes/price-list/_lib/components/modals/create-custom-items-modal";
import Page from "$routes/price-list/_lib/components/page-layout";
import SubmitButton from "$routes/price-list/_lib/components/submit-button";
import { useQuerystring } from "$routes/price-list/_lib/hooks/use-query-string";
import { isNotNullOrUndefined } from "$routes/price-list/_lib/predicates";
import { linkToPriceList, linkToPriceListHome } from "$routes/price-list/links";
import { gql } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { makeStyles, styled, Typography } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import {
  PriceableItemLanguage,
  PriceableItemOrigin,
  type UpsertPriceableItemInput,
} from "elephant-web/gql";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { useLocalStorage } from "usehooks-ts";
import {
  useRevenueDepartmentsAndTariffsQuery,
  useUpsertPricesAndPriceableItemsMutation,
} from "./__generated__/page.gql";
import type { AddItemsFixedRowData } from "./_lib/add-items-fixed-row-data";
import AddItemsTable from "./_lib/add-items-table";
import FinalisedItemsTable from "./_lib/finalised-items-table";
import { type AddItemsFormData, useFormSchema } from "./_lib/use-form-schema";
import useSearchNewItemLazyQuery from "./_lib/use-search-new-item-lazy-query/use-search-new-item-lazy-query";

gql`
  mutation upsertPricesAndPriceableItems($input: UpsertPricesAndPriceableItemsInput!) {
    priceListV2 {
      upsertPricesAndPriceableItems(input: $input) {
        totalItemsUpserted
        totalPricesUpserted
      }
    }
  }
`;

gql`
  query revenueDepartmentsAndTariffs {
    priceListV2 {
      revenueDepartments {
        edges {
          id
          name
        }
      }
      priceTariffs {
        edges {
          id
          name
        }
      }
    }
  }
`;

export const ERROR_MESSAGE = defineMessage({
  id: "priceList.addPrices.error.msg",
  defaultMessage: "Failed to add prices",
});

const useStyles = makeStyles(() => ({
  customItemButton: {
    flexShrink: 0,
  },
}));

const Header = styled(Typography)({});

const ButtonsAndFilters = styled("div")({
  display: "flex",
  flexWrap: "nowrap",
  flexDirection: "row",
  alignItems: "center",
});

const SearchBar = styled(_SearchBar)({});

const ContentWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const ButtonWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-end",
  gap: theme.spacing(2),
  paddingRight: theme.spacing(8),
  position: "sticky",
  bottom: 0,
}));

const AddItemsPage: FC = () => {
  const classes = useStyles();
  const { origin, tariffId } = useQuerystring();
  const navigate = useNavigate();
  const { facilityId } = useUser();
  const { id: priceListId } = useParams();
  const formSchema = useFormSchema();
  const { formatMessage } = useIntl();
  const { data: revDeptsAndTariffs, loading: revDeptsAndTariffsLoading } =
    useRevenueDepartmentsAndTariffsQuery();
  const revenueDepartments =
    revDeptsAndTariffs?.priceListV2.revenueDepartments.edges ?? [];
  const tariffs = revDeptsAndTariffs?.priceListV2.priceTariffs.edges ?? [];
  const [
    searchItems,
    { data: searchResults, loading: searchLoading, error: searchError },
  ] = useSearchNewItemLazyQuery(origin as PriceableItemOrigin);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 200);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useLocalStorage<number>(
    "priceList.addItems.pageSize",
    20,
  );

  const [creatingCustomItems, setCreatingCustomItems] = useState(false);
  const [isEditable, setIsEditable] = useState<boolean>(true);

  const [addPrices, { loading: addPricesLoading }] =
    useUpsertPricesAndPriceableItemsMutation({
      onCompleted: (data) => {
        toast.success(
          formatMessage(
            {
              id: "pricelist.addPrices.success",
              defaultMessage:
                "{totalPrices} {totalPrices, plural, one{price} other{prices}} added for {totalItems} {totalItems, plural, one{item} other{items}}",
            },
            {
              totalItems:
                data.priceListV2.upsertPricesAndPriceableItems
                  .totalItemsUpserted,
              totalPrices:
                data.priceListV2.upsertPricesAndPriceableItems
                  .totalPricesUpserted,
            },
          ),
        );
        navigate(`${linkToPriceListHome()}/${priceListId}`);
      },
      onError: (e) => {
        toast.error(`${formatMessage(ERROR_MESSAGE)}: ${e.message}`);
        setIsEditable(true);
      },
    });

  const tariff = useMemo(() => {
    if (revDeptsAndTariffsLoading) {
      return undefined;
    }
    const result = tariffs.find((r) => r.id === tariffId);
    if (!result) {
      toast.error(`No tariff with that id could be found: ${tariffId}`);
      navigate(linkToPriceListHome());
      return undefined;
    }
    return result;
  }, [tariffId, revDeptsAndTariffsLoading, navigate, tariffs.find]);

  const fixedRowData: AddItemsFixedRowData[] = useMemo(() => {
    return searchResults.rows.map((r) => ({
      ...r,
      // Dummy tariff only needed so object access while we're still fetching it's data doesn't break
      tariff: tariff ?? { id: "", name: "" },
    }));
  }, [searchResults, tariff]);

  useEffect(() => {
    searchItems({
      variables: {
        name: debouncedSearchTerm,
        pageSize,
        pageNumber,
        facilityId,
      },
    });
  }, [debouncedSearchTerm, pageNumber, pageSize, facilityId]);

  useEffect(() => {
    if (searchError) {
      toast.error(searchError.message);
    }
  }, [searchError]);

  const addItemsFormMethods = useForm<AddItemsFormData>({
    mode: "onTouched",
    resolver: yupResolver(formSchema),
  });

  useEffect(() => {
    if (!priceListId) {
      toast.error("Price list to add items to has not been specified.");
      navigate(linkToPriceListHome());
    }
  }, [priceListId, navigate]);
  if (!priceListId) {
    return null;
  }

  const onSubmit: Parameters<(typeof addItemsFormMethods)["handleSubmit"]>[0] =
    (data) => {
      const priceableItems: UpsertPriceableItemInput[] = Object.values(data)
        .filter(isNotNullOrUndefined)
        .map(
          ({
            name,
            externalReference,
            revenueDepartmentId,
            tariffId: rowTariffId,
            price,
          }) => ({
            name,
            externalReference,
            prices: [
              { revenueDepartmentId, tariffId: rowTariffId, value: price },
            ],
          }),
        );
      addPrices({
        variables: {
          input: {
            priceListId,
            priceableItems,
            language: PriceableItemLanguage.EnGb,
          },
        },
      });
    };

  return (
    <Page>
      <Header variant="h2">
        <FormattedMessage
          id="pricelist.addItems.header.title"
          defaultMessage="Elephant Items List"
        />
      </Header>

      <FormProvider {...addItemsFormMethods}>
        <form onSubmit={addItemsFormMethods.handleSubmit(onSubmit)}>
          {isEditable && (
            <ContentWrapper>
              <ButtonsAndFilters>
                {origin === PriceableItemOrigin.Custom && (
                  <Button
                    className={classes.customItemButton}
                    color="secondary"
                    onClick={() => setCreatingCustomItems(true)}
                    startIcon={<AddIcon />}
                    data-testid="priceList.createCustomItem.button"
                  >
                    <FormattedMessage
                      id="priceList.createCustomItem.button"
                      defaultMessage="Create Custom Item"
                    />
                  </Button>
                )}
                <SearchBar
                  fullWidth
                  hideHelper
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  onClear={() => {
                    setSearchTerm("");
                  }}
                  placeholder={formatMessage({
                    id: "priceList.addPrices.search",
                    defaultMessage: "Search items",
                  })}
                />
              </ButtonsAndFilters>
              <AddItemsTable
                fixedRowData={fixedRowData}
                revenueDepartments={revenueDepartments}
                pagination={{
                  page: pageNumber,
                  pageSize,
                  totalRows: searchResults.totalRows,
                  handlePageChange: (number) => setPageNumber(number),
                  pageSizeOptions: [20, 50, 100],
                  handlePageSizeChange: (newPageSize) => {
                    setPageSize(newPageSize);
                    setPageNumber(0);
                  },
                }}
                loading={searchLoading}
                dense
              />
              <ButtonWrapper>
                <Button
                  color="secondary"
                  onClick={() => navigate(linkToPriceList(priceListId))}
                  data-testid="priceList.addItemsPage.backToLandingPage.button"
                >
                  <FormattedMessage
                    id="priceList.addItemsPage.backToLandingPage.button"
                    defaultMessage="Back"
                  />
                </Button>
                <Button
                  color="primary"
                  onClick={() => {
                    setIsEditable(false);
                  }}
                  data-testid="priceList.addItemsPage.review.button"
                >
                  <FormattedMessage
                    id="priceList.addItemsPage.review.button"
                    defaultMessage="Review"
                  />
                </Button>
              </ButtonWrapper>
            </ContentWrapper>
          )}
          {!isEditable && (
            <ContentWrapper>
              <FinalisedItemsTable
                revenueDepartments={revenueDepartments}
                tariffs={tariffs}
              />
              <ButtonWrapper>
                <Button
                  color="secondary"
                  onClick={() => setIsEditable(true)}
                  data-testid="priceList.addItemsPage.back.button"
                >
                  <FormattedMessage
                    id="priceList.addItemsPage.back.button"
                    defaultMessage="Back"
                  />
                </Button>
                <SubmitButton
                  color="primary"
                  type="submit"
                  data-testid="priceList.addItemsPage.finalise.button"
                  loading={addPricesLoading}
                >
                  <FormattedMessage
                    id="priceList.addItemsPage.finalise.button"
                    defaultMessage="Finalise"
                  />
                </SubmitButton>
              </ButtonWrapper>
            </ContentWrapper>
          )}
        </form>
      </FormProvider>
      <CreateCustomItemsModal
        open={creatingCustomItems}
        onClose={() => setCreatingCustomItems(false)}
        priceListId={priceListId}
      />
    </Page>
  );
};

export default AddItemsPage;
